import React from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { ArrowRightIcon, ChevronDownIcon } from 'lucide-react';
import { trackAddToCart } from '../utils/metaPixel';
export function HeroSection() {
  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <section className="relative min-h-screen flex flex-col items-center justify-center pt-24 pb-20 px-6 overflow-hidden" data-id="element-104">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-[#E8636F] to-[#F4A261] rounded-full opacity-[0.03] blur-[100px] pointer-events-none" data-id="element-105"></div>

      <div className="max-w-6xl mx-auto w-full grid md:grid-cols-2 gap-12 items-center relative z-10" data-id="element-106">
        {/* Left Content */}
        <div className="text-center md:text-left" data-id="element-107">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="inline-flex items-center px-3 py-1 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm mb-6" data-id="element-108">
            <span className="text-xs font-bold text-white tracking-wide uppercase" data-id="element-109">
              14 días · Protocolo probado
            </span>
          </motion.div>

          <motion.h1 initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="text-5xl md:text-7xl font-heading font-extrabold text-white leading-[1.1] mb-6" data-id="element-110">
            Si estás sola, <br className="hidden md:block" data-id="element-111" />
            le escribes. <span className="text-gradient" data-id="element-112">Punto.</span>
          </motion.h1>

          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.4
        }} className="text-lg md:text-xl text-[#A8A8B3] mb-8 max-w-lg mx-auto md:mx-0 leading-relaxed" data-id="element-113">
            No te falta amor propio. Te falta un protocolo.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.6
        }} className="flex flex-col items-center md:items-start gap-4" data-id="element-114">
            <Button onClick={scrollToPricing} className="px-8 py-4 text-lg w-full md:w-auto" data-id="element-115">
              Quiero empezar hoy —{' '}
              <span className="line-through opacity-60 mr-1" data-id="element-116">$12</span> $6.36{' '}
              <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-117" />
            </Button>
            <span className="text-xs text-[#A8A8B3] uppercase tracking-wider" data-id="element-118">
              Descarga inmediata · PDF + Digital
            </span>
          </motion.div>
        </div>

        {/* Right Content - Mockup */}
        <motion.div initial={{
        opacity: 0,
        x: 60
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        duration: 1,
        delay: 0.4
      }} className="relative hidden md:block" data-id="element-119">
          <div className="absolute inset-0 bg-gradient-to-tr from-[#E8636F] to-[#F4A261] rounded-full opacity-10 blur-[60px]" data-id="element-120"></div>
          <img src="https://cdn.magicpatterns.com/uploads/bMfiQ7rRWoqVNjHTPgWiVx/mockup1.png" alt="No Contact Sin Recaídas Mockup" className="relative z-10 w-full rounded-2xl shadow-2xl shadow-black/50 transform hover:scale-[1.02] transition-transform duration-700" data-id="element-121" />
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1,
      y: [0, 10, 0]
    }} transition={{
      duration: 2,
      repeat: Infinity,
      delay: 1
    }} className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/30" data-id="element-122">
        <ChevronDownIcon className="w-6 h-6" data-id="element-123" />
      </motion.div>

      {/* Narrative Section */}
      <div className="max-w-2xl mx-auto mt-32 text-center md:text-left space-y-8 relative z-10" data-id="element-124">
        <motion.div initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 1
      }} className="space-y-6 text-lg md:text-xl text-[#A8A8B3] leading-relaxed" data-id="element-125">
          <p data-id="element-126">Estás bien todo el día.</p>
          <p data-id="element-127">Trabajas, haces tus cosas, hasta te convences de que ya pasó.</p>
          <p data-id="element-128">Y luego llega la noche. La casa se queda callada.</p>
          <p data-id="element-129">Tu pecho se aprieta. Y tu mano… va sola al teléfono.</p>
          <p data-id="element-130">No porque seas débil.</p>
          <p data-id="element-131">
            Sino porque tu cerebro aprendió algo:{' '}
            <span className="text-white font-bold" data-id="element-132">
              escribirle = alivio por un momento.
            </span>
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="py-12" data-id="element-133">
          <p className="text-lg text-[#A8A8B3] mb-2" data-id="element-134">
            El problema es lo que viene después:
          </p>
          <h3 className="text-3xl md:text-4xl font-heading font-bold text-gradient leading-tight" data-id="element-135">
            "¿Por qué hice esto otra vez?"
          </h3>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        x: -20
      }} whileInView={{
        opacity: 1,
        x: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.8
      }} className="pl-6 border-l-2 border-[#E8636F]" data-id="element-136">
          <p className="text-gradient font-bold italic text-lg mb-6" data-id="element-137">
            Déjame decirte esto con cariño:
          </p>
          <div className="space-y-2 text-xl md:text-2xl font-heading font-bold text-white" data-id="element-138">
            <p data-id="element-139">El impulso es físico.</p>
            <p data-id="element-140">Es ansiedad.</p>
            <p data-id="element-141">Es pecho apretado.</p>
            <p data-id="element-142">Es manos inquietas.</p>
          </div>
        </motion.div>
      </div>
    </section>;
}